// Builds an editable .docx of the Chairman briefing.
//
// Mirrors the dense single-column layout of
//   strategy/Bvndle-Chairman-Briefing-29May2026.docx
// but with today's date + the latest KPI copy. The Word file is the
// preferred export path when the chairman wants to mark up the doc; the
// in-app surface gives Print → PDF for a frozen copy.

import {
  AlignmentType,
  BorderStyle,
  Document,
  HeadingLevel,
  ImageRun,
  Packer,
  Paragraph,
  ShadingType,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
  type ISectionOptions,
} from "docx";

import { type LiveState } from "./live-category-stats";
import type {
  AdditionalCommitment,
  KpiBrief,
  KpiTrackOwner,
} from "./partner-rollout";

// Brand palette — sampled directly from
// strategy/Bvndle-Chairman-Briefing-29May2026.docx (the source design).
const PURPLE = "5E4AA8"; // supply band + DONE/LIVE pill bg
const PINK = "D14B8F";   // demand band + WIP/TESTING pill bg
const QUESTION_GREY = "6B6F76";
const INK = "1A1A1A";
const INK_SOFT = "3A3A3A";
const INK_FAINT = "6B6B6B";
const LINE = "D9D9D4";
const LINE_SOFT = "EBEAE5";
const BLOCKER = "A83232";

// Status pills — exact source hexes.
const STATUS_BG: Record<KpiTrackOwner["status"], string> = {
  done: PURPLE,
  live: PURPLE,
  wip: PINK,
  testing: PINK,
  pending: "E8E6EF",
  "not-started": "EFEEF3",
  blocked: "2E2E33",
};
const STATUS_FG: Record<KpiTrackOwner["status"], string> = {
  done: "FFFFFF",
  live: "FFFFFF",
  wip: "FFFFFF",
  testing: "FFFFFF",
  pending: "5B5B66",
  "not-started": "9AA0A6",
  blocked: "FFFFFF",
};

const ADDL_STATUS_BG: Record<AdditionalCommitment["status"], string> = {
  done: PURPLE,
  wip: PINK,
  pending: "E8E6EF",
  blocked: "2E2E33",
};
const ADDL_STATUS_FG: Record<AdditionalCommitment["status"], string> = {
  done: "FFFFFF",
  wip: "FFFFFF",
  pending: "5B5B66",
  blocked: "FFFFFF",
};

export interface ChairmanDocxData {
  generatedOn: string;
  sourcedFrom: string;
  supply: KpiBrief[];
  demand: KpiBrief[];
  additional: AdditionalCommitment[];
  liveState: LiveState;
  /** PNG dataURLs (data:image/png;base64,...) per KPI code, captured
   *  from the live chart at download time. Optional — if missing we
   *  fall back to a styled italic placeholder. */
  chartImages?: Record<string, string>;
}

export async function buildChairmanDocx(
  data: ChairmanDocxData,
): Promise<Blob> {
  const chartFor = (code: string): string | undefined =>
    data.chartImages?.[code];

  const children: ISectionOptions["children"] = [
    ...header(data),
    sideBanner("supply"),
    ...data.supply.flatMap((k, i) => [
      ...kpiBlockTable(k, "supply", chartFor(k.code)),
      ...(i < data.supply.length - 1 ? [kpiDivider()] : []),
    ]),
    sideBanner("demand"),
    ...data.demand.flatMap((k, i) => [
      ...kpiBlockTable(k, "demand", chartFor(k.code)),
      ...(i < data.demand.length - 1 ? [kpiDivider()] : []),
    ]),
    additionalHeader(),
    ...data.additional.flatMap(additionalBlock),
    footer(),
  ];

  const section: ISectionOptions = {
    properties: {
      page: { margin: { top: 720, right: 720, bottom: 720, left: 720 } },
    },
    children,
  };

  const doc = new Document({
    creator: "Bvndle Command Centre",
    title: "Bvndle KPIs & Track Owners — Chairman Briefing",
    description: `Generated ${data.generatedOn} · sourced from ${data.sourcedFrom}`,
    styles: {
      default: { document: { run: { font: "Calibri", size: 19 } } }, // 9.5pt
    },
    sections: [section],
  });
  return Packer.toBlob(doc);
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function header(data: ChairmanDocxData): Paragraph[] {
  return [
    new Paragraph({
      children: [
        new TextRun({
          text: "BVNDLE COMMAND CENTRE",
          color: INK_FAINT,
          size: 17,
          bold: true,
          characterSpacing: 30,
        }),
      ],
    }),
    new Paragraph({
      heading: HeadingLevel.HEADING_1,
      children: [
        new TextRun({
          text: "Bvndle KPIs & Track Owners",
          color: INK,
          bold: true,
          size: 36,
        }),
      ],
    }),
    new Paragraph({
      border: {
        bottom: { style: BorderStyle.SINGLE, size: 8, color: INK, space: 4 },
      },
      spacing: { after: 160 },
      children: [
        new TextRun({
          text:
            `Bvndle ManCo  ·  ${data.generatedOn}  ·  Sourced from ` +
            `${data.sourcedFrom}  ·  Live: ${data.liveState.toUpperCase()}`,
          color: INK_FAINT,
          size: 17,
        }),
      ],
    }),
  ];
}

// ---------------------------------------------------------------------------
// KPI block — 2-column docx Table.
//
//   LEFT column (62%):
//     Row 1: title band (purple/pink) + question
//     Row 2: chart placeholder
//     Rows 3..N: one row per track owner (name·scope · STATUS pill)
//                — FLAT, no shading
//
//   RIGHT column (38%):
//     ONE merged cell spanning all rows (vMerge "restart" → "continue")
//     containing OWNERS · INSIGHT · BLOCKERS · WHAT WE'RE DOING stacked
//     vertically.
// ---------------------------------------------------------------------------

function kpiBlockTable(
  kpi: KpiBrief,
  side: "supply" | "demand",
  chartPng?: string,
): (Table | Paragraph)[] {
  const sideFill = side === "supply" ? PURPLE : PINK;
  const out: (Table | Paragraph)[] = [];

  // Spacer paragraph before each block.
  out.push(spacer());

  const rows: TableRow[] = [];

  // Right column is built once and lives in row 1; rows 2..N have a
  // continuation cell with vMerge but no content.
  const rightCellTop = new TableCell({
    width: { size: 38, type: WidthType.PERCENTAGE },
    margins: { top: 80, bottom: 80, left: 140, right: 100 },
    borders: cellBorder(LINE),
    verticalMerge: "restart" as const,
    children: [
      metaLabel("OWNERS:"),
      new Paragraph({
        spacing: { after: 80 },
        children: [new TextRun({ text: kpi.owners, color: INK_SOFT, size: 18 })],
      }),
      metaLabel("ACTIONABLE INSIGHT:"),
      new Paragraph({
        spacing: { after: 80 },
        children: [
          new TextRun({
            text: kpi.actionableInsight,
            color: INK,
            bold: true,
            size: 18,
          }),
        ],
      }),
      ...(kpi.blockers
        ? [
            metaLabelColored("BLOCKERS:", BLOCKER),
            new Paragraph({
              spacing: { after: 80 },
              children: [
                new TextRun({ text: kpi.blockers, color: BLOCKER, size: 17 }),
              ],
            }),
          ]
        : []),
      metaLabel("WHAT WE'RE DOING ABOUT IT:"),
      new Paragraph({
        children: [new TextRun({ text: kpi.doing, color: INK_SOFT, size: 17 })],
      }),
    ],
  });

  // ROW 1 — header (left) | right column start
  rows.push(
    new TableRow({
      children: [
        new TableCell({
          width: { size: 62, type: WidthType.PERCENTAGE },
          margins: { top: 0, bottom: 0, left: 0, right: 0 },
          borders: cellBorder(LINE),
          children: [
            new Paragraph({
              shading: { type: ShadingType.CLEAR, color: "auto", fill: sideFill },
              spacing: { before: 60, after: 60 },
              indent: { left: 140, right: 100 },
              children: [
                new TextRun({
                  text: ` ${kpi.code} `,
                  color: "FFFFFF",
                  bold: true,
                  size: 16,
                  characterSpacing: 20,
                }),
                new TextRun({ text: "    ", size: 16 }),
                new TextRun({
                  text: kpi.title,
                  color: "FFFFFF",
                  bold: true,
                  size: 21,
                }),
              ],
            }),
            new Paragraph({
              spacing: { before: 80, after: 60 },
              indent: { left: 140, right: 100 },
              children: [
                new TextRun({
                  text: kpi.question,
                  color: QUESTION_GREY,
                  italics: true,
                  size: 17,
                }),
              ],
            }),
          ],
        }),
        rightCellTop,
      ],
    }),
  );

  // ROW 2 — chart (left, PNG if captured else italic placeholder)
  //         | right column continuation.
  const chartParagraph: Paragraph = chartPng
    ? new Paragraph({
        alignment: AlignmentType.CENTER,
        children: [
          new ImageRun({
            data: dataUrlToUint8(chartPng),
            transformation: { width: 380, height: 130 },
            type: "png",
          }),
        ],
      })
    : new Paragraph({
        alignment: AlignmentType.CENTER,
        children: [
          new TextRun({
            text: "[ chart · TODAY vs TARGET — see app or Print → PDF for the live visual ]",
            color: INK_FAINT,
            italics: true,
            size: 15,
          }),
        ],
      });
  rows.push(
    new TableRow({
      children: [
        new TableCell({
          width: { size: 62, type: WidthType.PERCENTAGE },
          margins: { top: 80, bottom: 80, left: 140, right: 100 },
          borders: cellBorder(LINE),
          children: [chartParagraph],
        }),
        rightContinuation(),
      ],
    }),
  );

  // ROWS 3..N — Track owners, flat (no shading). Right col continues merge.
  for (const o of kpi.trackOwners) {
    rows.push(
      new TableRow({
        children: [
          new TableCell({
            width: { size: 62, type: WidthType.PERCENTAGE },
            margins: { top: 50, bottom: 50, left: 140, right: 100 },
            borders: cellBorder(LINE),
            children: [
              new Paragraph({
                tabStops: [{ position: 8600, type: "right" as const }],
                children: [
                  new TextRun({ text: o.name, color: INK, bold: true, size: 17 }),
                  new TextRun({ text: "    ", size: 17 }),
                  new TextRun({ text: o.scope, color: INK_SOFT, size: 17 }),
                  new TextRun({ text: "\t", size: 17 }),
                  new TextRun({
                    text: ` ${o.status.replace("-", " ").toUpperCase()} `,
                    color: STATUS_FG[o.status],
                    bold: true,
                    size: 14,
                    shading: {
                      type: ShadingType.CLEAR,
                      color: "auto",
                      fill: STATUS_BG[o.status],
                    },
                    characterSpacing: 20,
                  }),
                ],
              }),
            ],
          }),
          rightContinuation(),
        ],
      }),
    );
  }

  out.push(
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows,
    }),
  );

  // No more below-table paragraphs — BLOCKERS + DOING are in the
  // right cell merged column above.
  return out;
}

// Helper: a continuation cell for the right column's vMerge.
function rightContinuation(): TableCell {
  return new TableCell({
    width: { size: 38, type: WidthType.PERCENTAGE },
    margins: { top: 0, bottom: 0, left: 140, right: 100 },
    borders: cellBorder(LINE),
    verticalMerge: "continue" as const,
    children: [spacer()],
  });
}

function spacer(): Paragraph {
  // Always return a Paragraph with at least one TextRun — docx-js can
  // serialise empty-children paragraphs but Word readers don't always
  // handle them, and we've seen the Word download error out when an
  // empty Paragraph appears anywhere in the section tree.
  return new Paragraph({
    spacing: { before: 0, after: 0 },
    children: [new TextRun({ text: "", size: 1 })],
  });
}

function metaLabelColored(text: string, color: string): Paragraph {
  return new Paragraph({
    spacing: { after: 20 },
    children: [
      new TextRun({
        text,
        color,
        bold: true,
        size: 14,
        characterSpacing: 20,
      }),
    ],
  });
}

// ---------------------------------------------------------------------------
// Side banner — gradient supply→demand bar with chip + title + subtitle.
// docx can't render true CSS gradients; we approximate with a solid
// brand-coloured row carrying the chip + title + subtitle text.
// ---------------------------------------------------------------------------
function sideBanner(side: "supply" | "demand"): Table {
  const fill = side === "supply" ? PURPLE : PINK;
  const chip = side === "supply" ? "SUPPLY" : "DEMAND";
  const title = side === "supply" ? "SUPPLY SIDE" : "DEMAND SIDE";
  const subtitle =
    side === "supply"
      ? "What moves the needle for issuance"
      : "What moves the needle for redemption";
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [
      new TableRow({
        children: [
          new TableCell({
            width: { size: 100, type: WidthType.PERCENTAGE },
            shading: { type: ShadingType.CLEAR, color: "auto", fill },
            margins: { top: 60, bottom: 60, left: 200, right: 200 },
            borders: cellBorder(fill),
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: ` ${chip}  `,
                    color: "FFFFFF",
                    bold: true,
                    size: 16,
                    characterSpacing: 30,
                  }),
                  new TextRun({
                    text: `   ${title}   `,
                    color: "FFFFFF",
                    bold: true,
                    size: 20,
                  }),
                  new TextRun({
                    text: `   ${subtitle}`,
                    color: "FFFFFF",
                    italics: true,
                    size: 17,
                  }),
                ],
              }),
            ],
          }),
        ],
      }),
    ],
  });
}

// ---------------------------------------------------------------------------
// KPI divider — thin gradient line. Approximated as a thin coloured row.
// ---------------------------------------------------------------------------
function kpiDivider(): Paragraph {
  return new Paragraph({
    spacing: { before: 100, after: 100 },
    border: {
      bottom: { style: BorderStyle.SINGLE, size: 8, color: PURPLE, space: 1 },
    },
    children: [new TextRun({ text: "", size: 1 })],
  });
}

function metaLabel(text: string): Paragraph {
  return new Paragraph({
    spacing: { after: 20 },
    children: [
      new TextRun({
        text,
        color: INK_FAINT,
        bold: true,
        size: 14,
        characterSpacing: 20,
      }),
    ],
  });
}

function cellBorder(color: string) {
  return {
    top: { style: BorderStyle.SINGLE, size: 4, color },
    bottom: { style: BorderStyle.SINGLE, size: 4, color },
    left: { style: BorderStyle.SINGLE, size: 4, color },
    right: { style: BorderStyle.SINGLE, size: 4, color },
  };
}

// ---------------------------------------------------------------------------
// Additional commitments
// ---------------------------------------------------------------------------

function additionalHeader(): Paragraph {
  return new Paragraph({
    spacing: { before: 200, after: 80 },
    border: {
      top: { style: BorderStyle.SINGLE, size: 8, color: INK, space: 6 },
    },
    children: [
      new TextRun({
        text: "ADDITIONAL COMMITMENTS",
        color: INK,
        bold: true,
        size: 20,
        characterSpacing: 30,
      }),
    ],
  });
}

function additionalBlock(c: AdditionalCommitment): Paragraph[] {
  return [
    new Paragraph({
      spacing: { before: 60, after: 20 },
      tabStops: [{ position: 8500, type: "right" as const }],
      children: [
        new TextRun({ text: c.title, color: INK, bold: true, size: 22 }),
        new TextRun({ text: "    ", size: 22 }),
        new TextRun({
          text: ` ${c.status.toUpperCase()} `,
          color: ADDL_STATUS_FG[c.status],
          bold: true,
          size: 14,
          shading: {
            type: ShadingType.CLEAR,
            color: "auto",
            fill: ADDL_STATUS_BG[c.status],
          },
          characterSpacing: 20,
        }),
        new TextRun({
          text: `   ${c.owner}`,
          color: INK_FAINT,
          size: 16,
        }),
      ],
    }),
    new Paragraph({
      spacing: { after: 80 },
      indent: { left: 0 },
      children: [
        new TextRun({ text: c.body, color: INK_SOFT, size: 18 }),
      ],
    }),
  ];
}

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

function footer(): Paragraph {
  return new Paragraph({
    spacing: { before: 240 },
    alignment: AlignmentType.CENTER,
    border: {
      top: { style: BorderStyle.SINGLE, size: 4, color: LINE, space: 4 },
    },
    children: [
      new TextRun({
        text: "BVNDLE COMMAND CENTRE      ·      POWERED BY VFD GROUP",
        color: INK_FAINT,
        size: 14,
        bold: true,
        characterSpacing: 30,
      }),
    ],
  });
}

// ---------------------------------------------------------------------------
// PNG dataURL → Uint8Array (for docx ImageRun)
// ---------------------------------------------------------------------------
function dataUrlToUint8(dataUrl: string): Uint8Array {
  const base64 = dataUrl.replace(/^data:image\/\w+;base64,/, "");
  // Browser-safe atob; the chairman page only runs in WebView so this
  // is always available.
  const bin = atob(base64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// Silence the linter for the unused colour constants we keep for parity
// with the in-app CSS palette.
void LINE_SOFT;
void Table;
void TableCell;
void TableRow;
void WidthType;
